import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import {
  CMSAsset,
  MediaListFilters,
  MediaListResponse,
} from '@/client/types/cms';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

export const mediaKeys = {
  all: ['cms', 'media'] as const,
  lists: () => [...mediaKeys.all, 'list'] as const,
  list: (page: number, filters: object) => [...mediaKeys.lists(), page, filters] as const,
};

const normalizeAssets = (raw: any): CMSAsset[] => {
  const list = Array.isArray(raw?.assets)
    ? raw.assets
    : Array.isArray(raw?.data)
      ? raw.data
      : Array.isArray(raw)
        ? raw
        : [];
  return list.map((a: any) => ({
    ...a,
    asid: a.asid,
    public_url: a.public_url ?? a.url ?? a.path,
    mime_type: a.mime_type ?? a.mime ?? a.content_type,
    filename: a.filename ?? a.name ?? a.original_filename,
  }));
};

const matchesMime = (asset: CMSAsset, mime?: MediaListFilters['mime']) => {
  if (!mime || mime === 'all') return true;
  const t = (asset.mime_type ?? '').toLowerCase();
  if (mime === 'image') return t.startsWith('image/');
  if (mime === 'video') return t.startsWith('video/');
  return true;
};

const useGetMediaListFn = () => {
  const request = useClientStore((state) => state.request);

  return withErrorHandling(
    async (page = 1, filters: MediaListFilters & { per_page?: number } = {}): Promise<MediaListResponse> => {
      const { per_page = 24, mime, ...rest } = filters;

      // Backend uses page_size (not per_page); mime is filtered client-side
      const params: Record<string, string> = {
        page: String(page),
        page_size: String(per_page),
      };

      for (const [key, value] of Object.entries(rest)) {
        if (value !== undefined && value !== null && value !== '' && value !== 'all') {
          params[key] = String(value);
        }
      }

      const searchParams = new URLSearchParams(params);
      const raw = await request<any>(`/api/admin/assets?${searchParams.toString()}`);
      const all = normalizeAssets(raw);
      const assets = all.filter((a) => matchesMime(a, mime));

      const total_assets = Number(raw?.total_assets ?? assets.length) || assets.length;
      const total_pages = Number(raw?.total_pages) || Math.max(1, Math.ceil(total_assets / per_page));

      return {
        assets,
        total_assets,
        page: Number(raw?.page ?? page),
        per_page: Number(raw?.per_page ?? per_page),
        total_pages,
      };
    },
    'An unexpected error occurred while fetching media',
  );
};

export const useMediaListQuery = (page = 1, filters: MediaListFilters = {}) => {
  const queryFn = useGetMediaListFn();
  return useQuery({
    queryKey: mediaKeys.list(page, filters),
    queryFn: () => queryFn(page, filters),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};
